/*
 * Tiny fetch wrapper for the K-Stack admin SPA.
 *
 * Auth model — same Gateway-native path AquaStar Admin uses:
 *   - JWT comes from POST /auth/login on Nexus.
 *   - We persist the accessToken in sessionStorage (tab-scoped — closing
 *     the tab signs you out; appropriate for an internal operator console).
 *   - Every API call carries Authorization: Bearer <accessToken>.
 *   - 401 from any endpoint clears the token + bubbles up; the route guard
 *     redirects to /login.
 *
 * The function `getToken` is exported because @nexus/admin-ui's
 * NexusAdminProvider takes `getAuthToken` as a prop and uses it on every
 * platform-page fetch.
 */

import { GATEWAY_BASE } from './config';

const TOKEN_KEY = 'kstack.admin.jwt';
/** NEXUS-586: the admin-console refresh token (its session ends after the admin MFA max age). */
const REFRESH_KEY = 'kstack.admin.rt';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}

export function setRefreshToken(token: string | null): void {
  if (token) sessionStorage.setItem(REFRESH_KEY, token);
  else sessionStorage.removeItem(REFRESH_KEY);
}

let refreshing: Promise<string | null> | null = null;

/**
 * NEXUS-586: swap the refresh token for a new access token (single-flight). The session
 * keeps its passkey step-up (mfa_at) until auth.admin_mfa.max_age_hours, and ends there;
 * then this returns null and the console signs in again. Never throws.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (refreshing) return refreshing;
  const rt = sessionStorage.getItem(REFRESH_KEY);
  if (!rt) return Promise.resolve(null);
  refreshing = (async () => {
    try {
      const res = await fetch(`${GATEWAY_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      const body = await readJson<{ accessToken?: string; refreshToken?: string }>(res);
      if (!res.ok || !body?.accessToken) {
        if (res.status === 401) { setRefreshToken(null); setToken(null); }
        return null;
      }
      setToken(body.accessToken);
      setRefreshToken(body.refreshToken ?? null);
      return body.accessToken;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function authHeader(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

interface LoginResponse {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
}

/**
 * POST /auth/login — Nexus core/auth credential exchange. Returns the
 * accessToken (JWT) which we stash in sessionStorage. The login page
 * is responsible for kicking AuthProvider.refresh() afterward.
 */
export async function login(email: string, password: string): Promise<void> {
  const res = await fetch(`${GATEWAY_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // NEXUS-586: an admin-console session — refreshable, but it ends after the admin MFA max age.
    body: JSON.stringify({ email, password, sessionKind: 'admin_console' }),
  });
  const body = await readJson<LoginResponse & { code?: string; message?: string }>(res);
  if (!res.ok) {
    const msg =
      (body && (body.message || body.code)) ||
      `Login failed (${res.status})`;
    throw new ApiError(res.status, msg, body);
  }
  if (!body?.accessToken) {
    throw new ApiError(res.status, 'Login succeeded but no accessToken returned', body);
  }
  setToken(body.accessToken);
  setRefreshToken(body.refreshToken ?? null);
}

export function logout(): void {
  setToken(null);
  setRefreshToken(null);
}

/**
 * Profile shape returned by GET /auth/me on Nexus. The K-Stack admin
 * console gates on is_platform_admin === true; anything else gets a
 * "not authorized" screen.
 */
export interface AuthMe {
  userId: string;
  email: string;
  displayName?: string | null;
  isPlatformAdmin: boolean;
  status?: string;
  /**
   * NEXUS-586: for platform admins — whether admin power needs a passkey step-up
   * (enforced) and whether this token has one (satisfied).
   */
  /** webHandoff: this session came from a Helm XR web hand-off — admin needs the passkey step-up,
   *  and an admin with no passkey must enrol one first (Erwin D2 2026-10-29). */
  adminMfa?: { enforced: boolean; satisfied: boolean; enrolled: boolean; webHandoff?: boolean } | null;
}

export async function getMe(): Promise<AuthMe> {
  const call = () => fetch(`${GATEWAY_BASE}/auth/me`, {
    method: 'GET',
    headers: { Accept: 'application/json', ...authHeader() },
  });
  let res = await call();
  // NEXUS-586: an expired access token is refreshed once (the step-up survives) before
  // the console gives up and shows the sign-in page.
  if (res.status === 401 && (await refreshAccessToken())) res = await call();
  const body = await readJson<Record<string, unknown>>(res);
  if (!res.ok) {
    if (res.status === 401) setToken(null);
    throw new ApiError(res.status, `GET /auth/me failed (${res.status})`, body);
  }
  // Defensive normalization: different Nexus versions have used both
  // is_platform_admin and isPlatformAdmin. Accept either.
  const isPlatformAdmin =
    Boolean(body['isPlatformAdmin']) || Boolean(body['is_platform_admin']);
  return {
    userId: String(body['userId'] ?? body['user_id'] ?? body['sub'] ?? ''),
    email: String(body['email'] ?? ''),
    displayName: (body['displayName'] as string | undefined) ?? (body['display_name'] as string | undefined) ?? null,
    isPlatformAdmin,
    status: body['status'] as string | undefined,
    adminMfa: (body['adminMfa'] as AuthMe['adminMfa']) ?? null,
  };
}

// ---------------------------------------------------------------- provider sign-in (NEXUS-586)

/** Sign-in providers the console offers, in display order. */
export type AdminOAuthProvider = 'apple' | 'google';
const ADMIN_PROVIDERS: AdminOAuthProvider[] = ['apple', 'google'];

/** The exact callback the gateway allowlists (auth.admin_console_redirect_uris). */
export const OAUTH_CALLBACK_PATH = '/oauth/callback';

/**
 * GET /auth/oauth/providers — which of Apple / Google this Nexus instance has
 * configured. Empty on any failure: the buttons simply do not show.
 */
export async function getOAuthProviders(): Promise<AdminOAuthProvider[]> {
  try {
    const res = await fetch(`${GATEWAY_BASE}/auth/oauth/providers`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const body = await readJson<{ providers?: unknown }>(res);
    const list = Array.isArray(body?.providers) ? (body.providers as unknown[]) : [];
    return ADMIN_PROVIDERS.filter((p) => list.includes(p));
  } catch {
    return [];
  }
}

/**
 * Where "Sign in with Apple/Google" sends the browser: the gateway's admin-mode
 * start, coming back to this origin's /oauth/callback. The gateway only accepts
 * a redirect_uri listed EXACTLY in its config, and only signs in an identity
 * already connected to a platform admin.
 */
export function adminOAuthStartUrl(provider: AdminOAuthProvider): string {
  const redirectUri = `${window.location.origin}${OAUTH_CALLBACK_PATH}`;
  return `${GATEWAY_BASE}/auth/oauth/${provider}?mode=admin&redirect_uri=${encodeURIComponent(redirectUri)}`;
}

/**
 * Store the access token from the provider callback exactly like a password
 * login does (sessionStorage only; the refresh token is not kept, as with
 * /auth/login).
 */
export function adoptOAuthAccessToken(accessToken: string): void {
  setToken(accessToken);
}
